import { isArray, isObject, size } from "lodash";

import type { WsAppSyncEvent } from "@storyboard/web-client";
import type { TestHarnessPubSub } from "@storyboard/web-client/test-harness";
import { Timings } from "@storyboard/web-logger/timings/Timings";
import { createLogger } from "@/logger";

const log = createLogger("appsync");

import { flags } from "@/config";
import { getQueueKey, queues } from "@/models/appSyncFunctions.ts";
import { startQueueProcessing } from "@/models/appsyncQueueProcessor";
import { bulkUpsertCallConfiguration } from "@/models/repositories/CallConfiguration";
import { extractSyncSignals, type SignalContext, type SyncSignals } from "@/models/sync/signals";
import { recentlyCreatedContentIds } from "@/models/sync/syncDelta";
import { setActiveSyncWorkspace, syncScheduler } from "@/models/sync/syncScheduler";
import type { SyncContext } from "@/models/sync/syncTargets";
import { recordEvent } from "@/services/telemetry";
import { idx, rowsByIndex } from "@/store/store";
import { handleEchoResponse, registerAppSyncEcho } from "./AppSyncEcho";

/** The account and workspace the live subscription serves; the resync triggers below act on it. */
let activeCtx: SyncContext | null = null;

/**
 * AppSync payload keys whose per-batch queue and process timings are dropped before Honeycomb.
 *
 * `asyncEchos` is the once-a-minute heartbeat, whose health the `Echo results` span and its unsampled failure events
 * already carry. `membershipAvailabilities` is the availability poll, whose queue timing lands as a root span of its
 * own — `createEvent` ends the span immediately — while the fetch it triggers keeps its web-client request span.
 */
const UNTIMED_APPSYNC_KEYS = new Set(["asyncEchos", "membershipAvailabilities"]);

function signalContext(ctx: SyncContext): SignalContext {
  return {
    accountId: ctx.accountId,
    isOrganizer: (accountId) =>
      rowsByIndex("workspaceMembership", idx.membershipsByAccount, accountId).some(
        (membership) => membership.workspaceId === ctx.workspaceId && membership.role === "member",
      ),
  };
}

/**
 * Walks every cursor table from where it stands, for the moments a gap may have opened. Content for items created in
 * the last few minutes is re-checked too, since a missed artifact signal is the one gap the items walk cannot heal.
 */
export async function resyncActiveWorkspace(): Promise<void> {
  if (!activeCtx) return;
  log.info("[APPSYNC] Resyncing after a possible gap", { workspaceId: activeCtx.workspaceId });
  await syncScheduler.resyncAll(activeCtx, recentlyCreatedContentIds());
}

/**
 * A hidden tab skips no drains, but a tab that was hidden or offline may have lost its subscription without the
 * error callback firing; regaining visibility or connectivity is the cheapest moment to check.
 */
function registerResyncTriggers(): void {
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") void resyncActiveWorkspace();
    });
  }
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => void resyncActiveWorkspace());
  }
}

function countRows(event: WsAppSyncEvent): Record<string, number> {
  return Object.entries(event).reduce<Record<string, number>>((memo, [key, val]) => {
    const cnt = isArray(val) || isObject(val) ? size(val) : 1;
    if (cnt > 0) memo[key] = cnt;
    return memo;
  }, {});
}

/**
 * Applies the few payload keys that are consumed from the wire rather than re-fetched.
 *
 * Echoes are the heartbeat and call configuration is ephemeral in-flight state with no read path — everything else in
 * the payload is only ever read for its identity fields. Pipeline artifact metadata is deprecated and ignored.
 */
async function applyWireFedKeys(event: WsAppSyncEvent): Promise<void> {
  for (const echo of event.asyncEchos ?? []) {
    handleEchoResponse(echo.data);
  }
  if (flags.allowInboundCalls && event.callConfiguration) {
    await bulkUpsertCallConfiguration([event.callConfiguration]);
  }
}

/**
 * Turns one drained AppSync batch into fetches: the payload's rows are read only for what they name, and the sync
 * layer walks each named table forward from its cursor, fetching content for the items that land.
 */
export async function appSyncSubscriptionUpdate(event: WsAppSyncEvent, myAccountId: string, workspaceId: string) {
  const timingSpans = Object.entries(countRows(event))
    .filter(([key]) => !UNTIMED_APPSYNC_KEYS.has(key))
    .map(([key, count]) =>
      Timings.startSpan(`AppSync - ${key}`, {
        category: "AppSync",
        telemetryCategory: "api",
        properties: { count },
      }),
    );

  try {
    await applyWireFedKeys(event);
    const ctx: SyncContext = { workspaceId, accountId: myAccountId };
    syncScheduler.markDirty(extractSyncSignals(event, signalContext(ctx)), ctx);
    await syncScheduler.flush();
  } finally {
    for (const timingSpan of timingSpans) {
      Timings.endSpan(timingSpan);
    }
  }
}

/**
 * Re-walks the items log for a workspace whose drained events the processor had to drop.
 *
 * The walk is cursor-driven, so which feeds the dropped events named does not matter: everything changed since the
 * items cursor comes back, content included, through the same pass a healthy drain takes.
 */
async function resyncItemsAfterDrop(_feedIds: string[], accountId: string, workspaceId: string): Promise<void> {
  log.info("[APPSYNC] Re-walking items after dropped events", { workspaceId });
  const signals: SyncSignals = {
    tables: new Set(["items"]),
    seeds: new Map(),
    eventItemIds: new Set(),
    contentIds: new Set(),
  };
  syncScheduler.markDirty(signals, { workspaceId, accountId });
  await syncScheduler.flush();
}

// Start the drain loop on module load, as the inline loop did before its extraction. The
// processor module owns the boot-settle pause (no drains while the cold-boot gate is active).
startQueueProcessing(appSyncSubscriptionUpdate, resyncItemsAfterDrop);
registerResyncTriggers();

let subscription: any;
let lastSubscriptionKey: string | undefined;

export async function subscribeToAppSync({
  accountId,
  currentWorkspaceId,
  pubSub,
  deviceRegistrationId,
}: {
  accountId: string;
  currentWorkspaceId: string;
  deviceRegistrationId: string;
  pubSub: TestHarnessPubSub;
}) {
  if (!pubSub) return;
  if (!accountId) return;

  const wkId = currentWorkspaceId || "";
  const key = getQueueKey(accountId, wkId);
  setActiveSyncWorkspace(wkId || null);
  activeCtx = wkId ? { workspaceId: wkId, accountId } : null;

  // Ensure there's a queue for this accountId/workspaceId pair
  if (!queues.has(key)) {
    queues.set(key, {
      accountId,
      workspaceId: wkId,
      events: [],
    });
  }

  const channels: string[] = [];
  channels.push(`wsaccount#${accountId}`);
  if (currentWorkspaceId) {
    channels.push(`workspace#${currentWorkspaceId}`);
    channels.push(`workspace_dispatch#${currentWorkspaceId}`);
  }
  // A replacement subscription for the *same* account/workspace mid-session means the previous
  // one dropped or went stale — a reliability signal for AppSync disconnect diagnostics (shipped
  // unsampled). Workspace switches also land here but are routine, so they don't emit.
  const resubscribing = Boolean(subscription) && key === lastSubscriptionKey;
  if (resubscribing) {
    recordEvent("appsync.resubscribe", { appsync: { channels: channels.length, workspaceId: wkId } });
  }
  lastSubscriptionKey = key;

  const newSub = pubSub.subscribeFilter(
    channels,
    (data?: any) => {
      if (data?.data) {
        const eventData = JSON.parse(data.data) as WsAppSyncEvent;
        const queue = queues.get(key);
        if (queue) {
          for (const [key, count] of Object.entries(countRows(eventData))) {
            if (UNTIMED_APPSYNC_KEYS.has(key)) continue;
            Timings.createEvent(`AppSync Queue - ${key}`, {
              category: "AppSync",
              telemetryCategory: "api",
              properties: { count },
            });
          }

          queue.events.push(eventData);
        } else {
          console.error(`No queue found for accountId=${accountId}, workspaceId=${currentWorkspaceId}`);
        }
      } else {
        console.warn("Account Feed Subscription Event called with no data", data);
      }
    },
    (e: any) => {
      console.error("Account Feed Subscription Error", e);
      recordEvent("appsync.subscription.error", {
        appsync: {
          channels: channels.length,
          workspaceId: wkId,
          error: e instanceof Error ? e.message : String(e?.error?.errors?.[0]?.message ?? e),
        },
      });
    },
  );
  subscription?.unsubscribe();
  subscription = newSub;

  registerAppSyncEcho(accountId, currentWorkspaceId, deviceRegistrationId);
  // The old subscription may have dropped payloads before anyone noticed; the cursors say exactly what to re-check.
  if (resubscribing) void resyncActiveWorkspace();
}
